import { ChangeEvent, FC } from "react";
import { FieldInputProps } from "react-final-form";
import { TSelectValue } from "@Types/common";
import classes from "./Select.module.scss";

export interface Option {
  value: string | number;
  text: string;
  disabled?: boolean;
  optgroup?: string;
}

type GroupedOptions = Record<
  string,
  { value: string | number; text: string }[]
>;

interface Props extends Partial<FieldInputProps<any, HTMLElement>> {
  options: Option[];
  selected: TSelectValue;
  disabled?: boolean;
  label?: string;
  id?: string;
  color?: string;
  className?: string;
  "data-testid"?: string;
  validate?: ((val: string | number) => boolean) | boolean;
}

export const Select: FC<Props> = ({
  options,
  selected,
  disabled,
  label,
  id,
  color,
  validate,
  className = "",
  ...rest
}) => {
  const groupedOptions: GroupedOptions = {};
  if (options?.length && options.every((o) => o.optgroup)) {
    options.forEach((option) => {
      if (!groupedOptions[option.optgroup!])
        groupedOptions[option.optgroup!] = [];
      groupedOptions[option.optgroup!].push({
        value: option.value,
        text: option.text,
      });
    });
  }

  const renderOptions = (options: Option[]) => {
    const optionsMarkup = options?.map((option, index) => {
      return (
        <option
          key={`${option.value}-${index}`}
          value={option.value}
          disabled={!!option.disabled}
          selected={selected === option.value}
        >
          {option.text}
        </option>
      );
    });
    optionsMarkup?.unshift(
      <option key={`${options.length}-${label}`} style={{ display: "none" }}>
        {label}
      </option>
    );
    return optionsMarkup;
  };

  const onChangeHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    if (rest.onChange) {
      rest.onChange(e);
    }
  };

  const isValid =
    validate && typeof validate === "function"
      ? validate(selected as string | number)
      : validate === null || validate === undefined
      ? true
      : validate;
  return (
    <div
      className={`${classes["select-container"]} ${
        color && isValid && classes[`input-${color}`]
      } ${className}`}
    >
      {selected !== null && selected !== undefined && (
        <label htmlFor={id}>{label}</label>
      )}
      <select
        disabled={disabled}
        value={selected as string | number}
        id={id}
        onChange={onChangeHandler}
        name={rest.name}
        data-testid={rest["data-testid"]}
      >
        {Object.keys(groupedOptions).length
          ? Object.keys(groupedOptions).map((group, index) => {
              return (
                <optgroup key={`${group}-${index}`} label={group}>
                  {renderOptions(groupedOptions[group])}
                </optgroup>
              );
            })
          : renderOptions(options)}
      </select>
    </div>
  );
};

Select.defaultProps = {
  label: "",
  disabled: false,
  id: undefined,
  color: "blue",
};
